import gTagConstants from '@/constants/gEvenets.constants';
import { useSession } from '@/context/SessionContext';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useRef, useEffect, useCallback } from 'react';
import type { SkuDTO } from '@/types/api/sku';

// Extended event params type to include all possible parameters
type EventParams = {
  slug: string;
  product_name?: string;
  product_category?: string;
  product_price?: number;
  page_type: string;
  price_bid?: number;
  price_ask?: number;
  brand_name_page?: string;
  country_name_page?: string;
};

// Base params for navigation events without product info
type NavigationEventParams = {
  page_type: string;
};

export default function useEvents(searchQuery: string, items: SkuDTO[]) {
  // Engagement time tracking
  const filterSearchFilterFilledEventEngagementTimeRef = useRef(0);
  const filterSearchFilterFilledEventTimerRef = useRef<NodeJS.Timeout | null>(null);
  const analytics = useAnalytics();
  const session = useSession();

  useEffect(() => {
    filterSearchFilterFilledEventTimerRef.current = setInterval(() => {
      filterSearchFilterFilledEventEngagementTimeRef.current += 1;
    }, 1000);
    return () => {
      if (filterSearchFilterFilledEventTimerRef.current) {
        clearInterval(filterSearchFilterFilledEventTimerRef.current);
        filterSearchFilterFilledEventTimerRef.current = null;
      }
    };
  }, []);

  const fireEvent = useCallback((eventName: string, params: Record<string, any>) => {
    if (typeof window !== 'undefined') {
      if (window.gtag) {
        window.gtag('event', eventName, params);
      } else if (window.dataLayer) {
        window.dataLayer.push({ event: eventName, ...params });
      }
    }
  }, []);

  const createBaseParams = useCallback(
    () => ({
      batch_ordering_id: analytics.getNextBatchOrderingId(),
      batch_page_id: analytics.getBatchPageId(),
      ga_session_number: analytics.getGASessionNumber(),
      ga_session_id: analytics.getGASessionId(),
      page_location: analytics.pageParams.page_location,
      page_referrer: analytics.pageParams.page_referrer,
      page_title: analytics.pageParams.page_title,
      engagement_time_sec: filterSearchFilterFilledEventEngagementTimeRef.current,
      device_category: analytics.getDeviceCategory(),
      session_id: session.sessionToken || '',
    }),
    [analytics, session.sessionToken]
  );

  // Create simplified base params for some events that don't need page info
  const createSimpleBaseParams = useCallback(
    () => ({
      batch_ordering_id: analytics.getNextBatchOrderingId(),
      batch_page_id: analytics.getBatchPageId(),
      ga_session_number: analytics.getGASessionNumber(),
      ga_session_id: analytics.getGASessionId(),
      engagement_time_sec: filterSearchFilterFilledEventEngagementTimeRef.current,
      device_category: analytics.getDeviceCategory(),
    }),
    [analytics]
  );

  const fireSearchBarFilterFilledEvent = useCallback(() => {
    const baseParams = createBaseParams();
    const params = {
      ...baseParams,
      search_term: searchQuery,
      results_shown: items.length,
      Category_filter: Array.from(
        new Set(items.map(item => item.category?.name || 'not_available'))
      ).join(','),
    };
    fireEvent(gTagConstants.eventKeys.search_bar_filled, params);
  }, [searchQuery, items, fireEvent, createBaseParams]);

  const fireSearchFilterFilledEvent = useCallback(() => {
    const baseParams = createBaseParams();
    const params = {
      ...baseParams,
      search_term: searchQuery,
      results_shown: items.length,
      Category_filter: Array.from(
        new Set(items.map(item => item.category?.name || 'not_available'))
      ).join(','),
    };
    fireEvent(gTagConstants.eventKeys.search_filter_filled, params);
  }, [searchQuery, items, fireEvent, createBaseParams]);

  // Generic function for product-related events
  const fireProductButtonClickEvent = useCallback(
    (
      eventName: string,
      {
        slug,
        product_name,
        product_category,
        product_price,
        page_type,
        price_bid,
        price_ask,
      }: EventParams
    ) => {
      const baseParams = createBaseParams();
      const params = {
        ...baseParams,
        page_type,
        Sku: slug,
        ...(product_name && { product_name }),
        ...(product_category && { product_category }),
        ...(product_price !== undefined && { Product_price: product_price }),
        ...(price_bid !== undefined && { Price_bid: price_bid }),
        ...(price_ask !== undefined && { Price_ask: price_ask }),
      };
      fireEvent(eventName, params);
    },
    [fireEvent, createBaseParams]
  );

  // Generic function for navigation events
  const fireNavigationEvent = useCallback(
    (
      eventName: string,
      { page_type, ...extraParams }: NavigationEventParams & Record<string, any>
    ) => {
      const baseParams = createBaseParams();
      const params = {
        ...baseParams,
        page_type,
        ...extraParams,
      };
      fireEvent(eventName, params);
    },
    [fireEvent, createBaseParams]
  );

  // Generic function for simple events that don't need extended parameters
  const fireSimpleEvent = useCallback(
    (eventName: string) => {
      const baseParams = createSimpleBaseParams();
      fireEvent(eventName, baseParams);
    },
    [fireEvent, createSimpleBaseParams]
  );

  // Product-related Events
  const fireBuyNowButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.buy_now_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  const firePlaceOrderButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.place_order_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  const firePlaceBidButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.place_bid_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  const fireBidConfirmButtonClickEvent = useCallback(
    (params: Omit<EventParams, 'price_bid'> & { price_bid: number }) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.bid_confirm_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  const fireSellNowButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.sell_now_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  // Implement all the new events as requested

  // 1. Place Ask Button Click
  const firePlaceAskButtonClickEvent = useCallback(
    (params: Omit<EventParams, 'price_ask'> & { price_ask: number }) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.place_ask_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  // 2. Confirm Sale Button Click
  const fireConfirmSaleButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.confirm_sale_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  // 3. Global To Bottle Button Click
  const fireGlobalToBottleButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.global_to_bottle_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  // Category Navigation Events
  const fireWhiskyCategoryButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.whisky_category_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireGinCategoryButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.gin_category_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireRumCategoryButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.rum_category_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireOtherCategoryButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.other_category_button_click, params);
    },
    [fireNavigationEvent]
  );

  // Brand/Country Navigation
  const fireGlobalBrandButtonClickEvent = useCallback(
    (params: NavigationEventParams & { brand_name_page: string }) => {
      fireNavigationEvent(gTagConstants.eventKeys.global_brand_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireGlobalCountryButtonClickEvent = useCallback(
    (params: NavigationEventParams & { country_name_page: string }) => {
      fireNavigationEvent(gTagConstants.eventKeys.global_country_button_click, params);
    },
    [fireNavigationEvent]
  );

  // Newsletter/Social Media Events
  const fireNewsletterSignupButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.newsletter_signup_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireInstagramButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.instagram_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireTiktokButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.tiktok_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireFacebookButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.facebook_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireYoutubeButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.youtube_button_click, params);
    },
    [fireNavigationEvent]
  );

  // Wishlist/Portfolio Events
  const fireWishlistButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.wishlist_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireAddWishlistButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.add_wishlist_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  const firePortfolioButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.portfolio_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireAddPortfolioButtonClickEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.add_portfolio_button_click, params);
    },
    [fireProductButtonClickEvent]
  );

  // Authentication Events
  const fireSignInButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.sign_in_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireSignUpButtonClickEvent = useCallback(
    (params: NavigationEventParams) => {
      fireNavigationEvent(gTagConstants.eventKeys.sign_up_button_click, params);
    },
    [fireNavigationEvent]
  );

  const fireGlobalSignUpEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.global_sign_up);
  }, [fireSimpleEvent]);

  // Seller Events
  const fireBecomeSellerButtonClickEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.become_seller_button_click);
  }, [fireSimpleEvent]);

  const fireGlobalSellingSignUpEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.global_selling_sign_up);
  }, [fireSimpleEvent]);

  // Page View Events
  const fireMatchPageViewEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.match_page_view);
  }, [fireSimpleEvent]);

  const fireAuthenticationPageViewEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.authentication_page_view);
  }, [fireSimpleEvent]);

  const firePayoutPageViewEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.payout_page_view);
  }, [fireSimpleEvent]);

  // Purchase Event
  const firePurchaseEvent = useCallback(
    (params: EventParams) => {
      fireProductButtonClickEvent(gTagConstants.eventKeys.purchase, params);
    },
    [fireProductButtonClickEvent]
  );

  // Arrival Confirmation
  const fireArrivalConfirmedButtonClickEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.arrival_confirmed_button_click);
  }, [fireSimpleEvent]);

  // Session Start Event
  const fireSessionStartEvent = useCallback(() => {
    fireSimpleEvent(gTagConstants.eventKeys.session_start);
  }, [fireSimpleEvent]);

  return {
    fireSearchBarFilterFilledEvent,
    fireSearchFilterFilledEvent,
    fireBuyNowButtonClickEvent,
    firePlaceOrderButtonClickEvent,
    firePlaceBidButtonClickEvent,
    fireBidConfirmButtonClickEvent,
    fireSellNowButtonClickEvent,
    firePlaceAskButtonClickEvent,
    fireConfirmSaleButtonClickEvent,
    fireGlobalToBottleButtonClickEvent,
    fireWhiskyCategoryButtonClickEvent,
    fireGinCategoryButtonClickEvent,
    fireRumCategoryButtonClickEvent,
    fireOtherCategoryButtonClickEvent,
    fireGlobalBrandButtonClickEvent,
    fireGlobalCountryButtonClickEvent,
    fireNewsletterSignupButtonClickEvent,
    fireInstagramButtonClickEvent,
    fireTiktokButtonClickEvent,
    fireFacebookButtonClickEvent,
    fireYoutubeButtonClickEvent,
    fireWishlistButtonClickEvent,
    fireAddWishlistButtonClickEvent,
    firePortfolioButtonClickEvent,
    fireAddPortfolioButtonClickEvent,
    fireSignInButtonClickEvent,
    fireSignUpButtonClickEvent,
    fireGlobalSignUpEvent,
    fireBecomeSellerButtonClickEvent,
    fireGlobalSellingSignUpEvent,
    fireMatchPageViewEvent,
    fireAuthenticationPageViewEvent,
    firePayoutPageViewEvent,
    firePurchaseEvent,
    fireArrivalConfirmedButtonClickEvent,
    fireSessionStartEvent,
  };
}
